import { useState, type FormEvent } from 'react';
import { useAuth } from '../store/auth';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { apiClient } from '../lib/api';
import { Lock, User, Globe, CheckCircle } from 'lucide-react';

export function ProfilePage() {
  const { user, fetchMe } = useAuth();
  const [email, setEmail] = useState(user?.email ?? '');
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [timezone, setTimezone] = useState(user?.timezone ?? 'UTC');
  const [savedProfile, setSavedProfile] = useState(false);

  const [currentPwd, setCurrentPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState<{
    type: 'ok' | 'err';
    msg: string;
  } | null>(null);

  async function saveProfile(e: FormEvent) {
    e.preventDefault();
    await apiClient.patch('/api/v1/auth/me', { email, displayName, timezone });
    setSavedProfile(true);
    fetchMe();
    setTimeout(() => setSavedProfile(false), 2000);
  }

  async function changePwd(e: FormEvent) {
    e.preventDefault();
    setPwdMsg(null);
    if (newPwd.length < 8)
      return setPwdMsg({ type: 'err', msg: 'New password must be ≥ 8 chars' });
    if (newPwd !== confirmPwd)
      return setPwdMsg({ type: 'err', msg: 'Passwords do not match' });
    try {
      await apiClient.post('/api/v1/auth/change-password', {
        currentPassword: currentPwd,
        newPassword: newPwd,
      });
      setPwdMsg({ type: 'ok', msg: 'Password updated successfully' });
      setCurrentPwd('');
      setNewPwd('');
      setConfirmPwd('');
    } catch (err) {
      setPwdMsg({ type: 'err', msg: (err as Error).message });
    }
  }

  return (
    <div className="w-full space-y-5 sm:space-y-6 animate-fade-in max-w-3xl">
      <div>
        <h1 className="font-display text-2xl font-bold uppercase tracking-widest flex items-center gap-2">
          <User size={22} className="text-neon-cyan" />
          Profile
        </h1>
        <p className="text-sm text-text-secondary font-mono mt-1">
          // {user?.username} · {user?.role}
        </p>
      </div>

      <Card>
        <h2 className="font-display text-sm uppercase tracking-wider text-neon-cyan flex items-center gap-2 mb-4">
          <User size={16} /> Profile Info
        </h2>
        <form onSubmit={saveProfile} className="w-full space-y-4">
          <Input label="Username" value={user?.username ?? ''} disabled />
          <Input
            label="Display Name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {/* Timezone — was free-text (easy typo into invalid IANA zone
              that server rejected). Now a curated select of common zones
              that cover the network's publisher base. To add more: append
              to the list. Values are IANA Time Zone identifiers. */}
          <div>
            <label className="block text-[10px] font-display uppercase tracking-wider text-text-secondary mb-1.5 flex items-center gap-1.5">
              <Globe size={14} /> Timezone
            </label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="input-cyber w-full text-xs"
            >
              <option value="UTC">UTC</option>
              <option value="Asia/Jakarta">Asia/Jakarta (WIB)</option>
              <option value="Asia/Makassar">Asia/Makassar (WITA)</option>
              <option value="Asia/Jayapura">Asia/Jayapura (WIT)</option>
              <option value="Asia/Kuala_Lumpur">Asia/Kuala_Lumpur</option>
              <option value="Asia/Singapore">Asia/Singapore</option>
              <option value="Asia/Bangkok">Asia/Bangkok</option>
              <option value="Asia/Manila">Asia/Manila</option>
              <option value="Asia/Tokyo">Asia/Tokyo</option>
              <option value="Asia/Shanghai">Asia/Shanghai</option>
              <option value="Asia/Dubai">Asia/Dubai</option>
              <option value="Europe/London">Europe/London</option>
              <option value="Europe/Berlin">Europe/Berlin</option>
              <option value="America/New_York">America/New_York</option>
              <option value="America/Los_Angeles">America/Los_Angeles</option>
              <option value="Australia/Sydney">Australia/Sydney</option>
            </select>
            <p className="text-xs text-text-tertiary mt-1 font-mono">
              Reporting, Today/Yesterday, and Daily Breakdown use UTC globally.
            </p>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button type="submit">Save Profile</Button>
            {savedProfile && (
              <span className="text-neon-green text-xs flex items-center gap-1 font-mono">
                <CheckCircle size={14} /> saved
              </span>
            )}
          </div>
        </form>
      </Card>

      <Card>
        <h2 className="font-display text-sm uppercase tracking-wider text-neon-magenta flex items-center gap-2 mb-4">
          <Lock size={16} /> Change Password
        </h2>
        <form onSubmit={changePwd} className="w-full space-y-4">
          <Input
            label="Current Password"
            type="password"
            value={currentPwd}
            onChange={(e) => setCurrentPwd(e.target.value)}
            required
          />
          <Input
            label="New Password"
            type="password"
            value={newPwd}
            onChange={(e) => setNewPwd(e.target.value)}
            hint="≥ 8 chars"
            required
          />
          <Input
            label="Confirm New Password"
            type="password"
            value={confirmPwd}
            onChange={(e) => setConfirmPwd(e.target.value)}
            required
          />
          {pwdMsg && (
            <div
              className={`text-xs font-mono ${pwdMsg.type === 'ok' ? 'text-neon-green' : 'text-neon-red'}`}
            >
              {pwdMsg.msg}
            </div>
          )}
          <div className="pt-2">
            <Button type="submit" variant="magenta">
              Update Password
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
